import { FC, useCallback, useMemo, useState } from 'react';
import { ChevronDownIcon } from '@shared/assets/icons';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { Checkbox } from '@shared/ui/kit';
import { EmployeeList } from '../employee-list';
import { DepartmentNode } from '@features/add-participants-modal/model/useFetchEmployeesTree';
import { InvitedParticipants } from '@widgets/segments-tab';

function employeeMatchesSearch(
  employee: { firstName: string; lastName: string; position?: string },
  search: string
): boolean {
  const searchLower = search.toLowerCase().trim();
  if (!searchLower) return true;
  const firstName = employee.firstName?.toLowerCase().trim() || '';
  const lastName = employee.lastName?.toLowerCase().trim() || '';
  const fullName = `${firstName} ${lastName}`;
  const reversedName = `${lastName} ${firstName}`;
  return (
    fullName.includes(searchLower) ||
    reversedName.includes(searchLower) ||
    firstName.includes(searchLower) ||
    lastName.includes(searchLower)
  );
}

function departmentMatchesSearch(department: DepartmentNode, search: string): boolean {
  if (!search?.trim()) return false;
  const searchLower = search.toLowerCase().trim();
  const departmentNameMatch = (department.name ?? '').toLowerCase().trim().includes(searchLower);
  const employeeMatch = (department.employees ?? []).some((employee) => employeeMatchesSearch(employee, searchLower));
  const childMatch = (department.childDepartments ?? []).some((child) => departmentMatchesSearch(child, searchLower));
  return departmentNameMatch || employeeMatch || childMatch;
}

interface DepartmentTreeProps {
  department: DepartmentNode;
  searchString?: string;
  selectedParticipants: number[];
  setSelectedParticipants: (users: number[]) => void;
  invitedParticipants: InvitedParticipants[];
  participantsDelete?: InvitedParticipants[];
  setParticipantsDelete?: React.Dispatch<React.SetStateAction<InvitedParticipants[]>>;
}

export const EmployeeTree: FC<DepartmentTreeProps> = ({
  department,
  searchString,
  selectedParticipants,
  setSelectedParticipants,
  invitedParticipants,
  participantsDelete,
  setParticipantsDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSearch = !!searchString?.trim();
  const departmentHasMatch = useMemo(
    () => departmentMatchesSearch(department, searchString ?? ''),
    [department, searchString]
  );
  const isEffectivelyOpen = (hasSearch && departmentHasMatch) || isOpen;
  const arrowStyle = isEffectivelyOpen ? styles.chevronOpen : styles.chevronClosed;
  const departmentNameMatchesSearch =
    hasSearch &&
    (department.name ?? '')
      .toLowerCase()
      .trim()
      .includes((searchString ?? '').toLowerCase().trim());

  const collectIds = (department: DepartmentNode): number[] => {
    const own = (department.employees ?? []).map((employee) => employee.id);
    const child = (department.childDepartments ?? []).flatMap(collectIds);
    return [...own, ...child];
  };

  const isInvited = useMemo(() => new Set(invitedParticipants.map((p) => p.userId)), [invitedParticipants]);
  const inDelete = useMemo(() => new Set(participantsDelete?.map((p) => p.userId)), [participantsDelete]);
  const inSelected = useMemo(() => new Set(selectedParticipants), [selectedParticipants]);

  const isChecked = useCallback(
    (id: number) => (isInvited.has(id) && !inDelete.has(id)) || inSelected.has(id),
    [isInvited, inDelete, inSelected]
  );

  const handleEmployeeToggle = useCallback(
    (id: number) => {
      if (isInvited.has(id)) {
        setParticipantsDelete?.((prev) =>
          prev.some((p) => p.userId === id)
            ? prev.filter((p) => p.userId !== id)
            : [...prev, invitedParticipants.find((p) => p.userId === id)!]
        );
      } else {
        setSelectedParticipants(
          inSelected.has(id) ? selectedParticipants.filter((v) => v !== id) : [...selectedParticipants, id]
        );
      }
    },
    [isInvited, inSelected, invitedParticipants, selectedParticipants, setSelectedParticipants, setParticipantsDelete]
  );

  const allIds = useMemo(() => collectIds(department), [department]);
  const allSelected = allIds.length > 0 && allIds.every(isChecked);
  const indeterminate = !allSelected && allIds.some(isChecked);

  const filteredEmployees = useMemo(() => {
    if (!hasSearch) return department.employees ?? [];
    if (departmentNameMatchesSearch) return department.employees ?? [];
    return (department.employees ?? []).filter((employee) => employeeMatchesSearch(employee, searchString ?? ''));
  }, [department.employees, hasSearch, searchString, departmentNameMatchesSearch]);

  const handleDepartmentCheckboxChange = useCallback(() => {
    const invitedIds = allIds.filter((id) => isInvited.has(id));
    const nonInvitedIds = allIds.filter((id) => !isInvited.has(id));
    if (allSelected) {
      setParticipantsDelete?.((prev) => {
        const next = [...prev];
        for (const id of invitedIds) {
          if (!next.some((p) => p.userId === id)) {
            const found = invitedParticipants.find((p) => p.userId === id);
            if (found) next.push(found);
          }
        }
        return next;
      });
      setSelectedParticipants(selectedParticipants.filter((id) => !nonInvitedIds.includes(id)));
    } else {
      setParticipantsDelete?.((prev) => prev.filter((p) => !invitedIds.includes(p.userId)));
      const next = Array.from(new Set([...selectedParticipants, ...nonInvitedIds]));
      setSelectedParticipants(next);
    }
  }, [
    allIds,
    isInvited,
    allSelected,
    invitedParticipants,
    selectedParticipants,
    setParticipantsDelete,
    setSelectedParticipants,
  ]);

  return (
    <>
      <div onClick={() => setIsOpen(!isOpen)} {...stylex.props(styles.departmentHeader)}>
        <div {...stylex.props(styles.departmentHeaderContent)}>
          <span {...stylex.props(styles.departmentArrow, arrowStyle)}>
            <ChevronDownIcon size={20} />
          </span>
          <span {...stylex.props(styles.checkbox)} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              isChecked={allSelected}
              isIndeterminate={indeterminate}
              onChange={handleDepartmentCheckboxChange}
            />
          </span>
          <span {...stylex.props(styles.departmentTitle)}>{department.name}</span>
        </div>
      </div>

      {isEffectivelyOpen && (
        <div {...stylex.props(styles.departmentNode)}>
          <div {...stylex.props(styles.departmentChildren)}>
            <EmployeeList employees={filteredEmployees} isChecked={isChecked} onToggle={handleEmployeeToggle} />
            {(department.childDepartments ?? []).map((child) => (
              <EmployeeTree
                key={child.id}
                department={child}
                searchString={searchString}
                selectedParticipants={selectedParticipants}
                setSelectedParticipants={setSelectedParticipants}
                invitedParticipants={invitedParticipants}
                participantsDelete={participantsDelete}
                setParticipantsDelete={setParticipantsDelete}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};
